import { toast } from "sonner";
import { log } from "@/lib/logger";

type AnyError = {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
  status?: number;
} | Error | unknown;

const NOISY_PREFIXES = [
  "JWT", "JSON object requested", "duplicate key value",
  "violates row-level security", "permission denied",
];

/** Sanitize PostgREST / Supabase errors into a user-friendly PT-BR message. */
export function getFriendlyErrorMessage(error: AnyError, fallback: string): string {
  if (!error) return fallback;
  const msg = (error as { message?: string }).message ?? String(error);

  if (!msg || NOISY_PREFIXES.some((p) => msg.includes(p))) return fallback;
  if (msg.length > 200) return fallback;
  // Strip technical prefixes
  return msg.replace(/^(Error:|TypeError:|PostgrestError:)\s*/i, "");
}

interface HandleOptions {
  /** Suppress toast — only log. Use for background/non-critical operations. */
  silent?: boolean;
  /** Override the toast title. */
  title?: string;
  /** Extra structured context for the logger. */
  context?: Record<string, unknown>;
}

/**
 * Standard error handler for Supabase calls.
 * - Logs via structured logger (redacted).
 * - Shows a PT-BR toast unless `silent: true`.
 * - Returns the (sanitized) message used.
 */
export function handleSupabaseError(
  error: AnyError,
  fallbackMessage = "Não foi possível completar a operação.",
  options: HandleOptions = {}
): string {
  const friendly = getFriendlyErrorMessage(error, fallbackMessage);

  log.error(
    {
      err: {
        message: (error as { message?: string })?.message,
        code: (error as { code?: string })?.code,
        status: (error as { status?: number })?.status,
      },
      ...options.context,
    },
    "supabase.error"
  );

  if (!options.silent) {
    toast.error(options.title ?? fallbackMessage, {
      description: friendly !== fallbackMessage ? friendly : undefined,
    });
  }

  return friendly;
}
